import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Pencil } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import { useGoBack } from "../../shared/hooks/goBack";
import type { MeetingDetail } from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  InlineAlert,
  LoadingState,
  StatusBadge,
} from "../../shared/ui";
import { AttachmentsCard } from "../attachments/AttachmentsCard";
import { formatTimestamp } from "../workitems/workItemVocabulary";
import { CancelMeetingDrawer } from "./MeetingDrawers";
import { MinutesEditor } from "./MinutesEditor";
import { ScheduleMeetingDrawer } from "./ScheduleMeetingDrawer";
import { SubmittedMinutes } from "./SubmittedMinutes";
import { ATTENDANCE_LABELS, PHASE_LABELS, PHASE_TONE, formatMeetingTime, phaseOf } from "./meetingVocabulary";

export function MeetingDetailPage() {
  const { projectId = "", meetingId = "" } = useParams();
  const { goBack, hasHistory } = useGoBack(`/projects/${projectId}?tab=meetings`);
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const meeting = useQuery({
    queryKey: ["meeting", meetingId],
    queryFn: () => api.get<MeetingDetail>(`/api/v1/projects/${projectId}/meetings/${meetingId}`),
  });

  /** Every write returns the whole meeting; show it at once and let the list catch up. */
  const apply = (updated: MeetingDetail) => {
    queryClient.setQueryData(["meeting", meetingId], updated);
    void queryClient.invalidateQueries({ queryKey: ["meetings", projectId] });
  };
  const base = `/api/v1/projects/${projectId}/meetings/${meetingId}`;

  if (meeting.isLoading) {
    return <LoadingState />;
  }
  if (meeting.error || !meeting.data) {
    return <ErrorState error={meeting.error} onRetry={() => void meeting.refetch()} />;
  }

  const data = meeting.data;
  const phase = phaseOf(data);

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={goBack}
        className="inline-flex items-center gap-1.5 text-sm text-ink-600 hover:text-ink-900"
      >
        <ArrowLeft className="h-4 w-4" />
        {hasHistory ? "Back" : data.projectName}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl text-ink-900">{data.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-ink-500">
            <StatusBadge tone={PHASE_TONE[phase]}>{PHASE_LABELS[phase]}</StatusBadge>
            <span>{formatMeetingTime(data.startsAt, data.endsAt)}</span>
            <span>· Organised by {data.organiser.fullName}</span>
          </div>
        </div>
        {data.status === "SCHEDULED" && can("MEETING_MANAGE") && (
          <div className="flex gap-2">
            <Button variant="secondary" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button variant="ghost" onClick={() => setCancelling(true)}>
              Cancel meeting
            </Button>
          </div>
        )}
      </div>

      {data.status === "CANCELLED" && (
        <InlineAlert tone="warning">
          Cancelled {data.cancelledAt ? formatTimestamp(data.cancelledAt) : ""}: {data.cancelReason}
        </InlineAlert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Agenda" subtitle={data.location ? `Where: ${data.location}` : undefined} />
            <CardBody>
              <p className="whitespace-pre-wrap text-sm text-ink-800">{data.agenda || "No agenda was set."}</p>
            </CardBody>
          </Card>

          {data.status === "SCHEDULED" && data.canRecordMinutes && (
            <MinutesEditor
              meeting={data}
              canSaveDraft={can("MEETING_MANAGE")}
              onSaveDraft={async (body) => apply(await api.put<MeetingDetail>(`${base}/minutes-draft`, body))}
              onSubmit={async (body) => apply(await api.post<MeetingDetail>(`${base}/minutes`, body))}
            />
          )}
          {data.status === "SCHEDULED" && !data.canRecordMinutes && (
            <InlineAlert tone="info">The organiser records the minutes once the meeting has happened.</InlineAlert>
          )}
          {data.status === "COMPLETED" && (
            <SubmittedMinutes
              meeting={data}
              onTick={async (point, done) =>
                apply(await api.put<MeetingDetail>(`${base}/points/${point.id}/done`, { done }))
              }
              onMakeWorkItem={async (point, workItemType) =>
                apply(await api.post<MeetingDetail>(`${base}/points/${point.id}/work-item`, { workItemType }))
              }
              onCorrect={async (body) => apply(await api.post<MeetingDetail>(`${base}/corrections`, { body }))}
            />
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader title="People" subtitle={`${data.attendees.length} asked`} />
            <ul className="divide-y divide-ink-100">
              {data.attendees.map((a) => (
                <li key={a.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                  <span className="min-w-0 truncate">
                    {a.name}
                    {a.guest && <span className="text-ink-400"> · guest</span>}
                  </span>
                  {data.status === "COMPLETED" && (
                    <StatusBadge tone={a.attendance === "PRESENT" ? "green" : "gray"}>
                      {ATTENDANCE_LABELS[a.attendance]}
                    </StatusBadge>
                  )}
                </li>
              ))}
            </ul>
          </Card>
          <AttachmentsCard
            entityType="MEETING"
            entityId={data.id}
            emptyMessage="Slides, a photo of the whiteboard, the document everyone was looking at."
            canRemove={data.status !== "COMPLETED"}
          />
        </div>
      </div>

      <ScheduleMeetingDrawer
        projectId={projectId}
        meeting={data}
        open={editing}
        onClose={() => setEditing(false)}
        onSaved={apply}
      />
      <CancelMeetingDrawer
        open={cancelling}
        onClose={() => setCancelling(false)}
        onCancel={async (reason) => apply(await api.post<MeetingDetail>(`${base}/cancel`, { reason }))}
      />
    </div>
  );
}
